import React, { FC, ReactElement } from 'react'
import { includes } from 'lodash'
import { useTranslation } from 'react-i18next'
import { dividedDirectorsCategories } from '../../constants'
import { LegalEntityDirector } from '../../graphql'
import { directorsStateVar } from '../../graphql/local'
import { usePageFiltersSorting } from '../../hooks'
import { TableDivider } from '../Common/TableDivider'
import { DirectorsSortBy } from './types'

export const DirectorsTableDivider: FC<{
  directors: LegalEntityDirector[]
  index: number
  children: ReactElement
}> = ({ directors, index, children }) => {
  const { t } = useTranslation()

  const { pageFilters } = usePageFiltersSorting(directorsStateVar)
  const { sortBy } = pageFilters

  if (!includes(dividedDirectorsCategories, sortBy) || directors.length <= 5) {
    return <>{children}</>
  }

  const prevDirector = directors[index - 1]
  const currentDirector = directors[index]
  const nextDirector = directors[index + 1]

  const isNotLastRow = index + 1 !== directors.length

  const dividerFactory = {
    [DirectorsSortBy.role]: () => ({
      isShowDivider: currentDirector?.role !== prevDirector?.role,
      isBorderBottomHidden: currentDirector?.role !== nextDirector?.role && isNotLastRow,
      dividerText: t(currentDirector?.role || ''),
    }),

    [DirectorsSortBy.signingRight]: () => ({
      isShowDivider: currentDirector?.signingRight !== prevDirector?.signingRight,
      isBorderBottomHidden:
        currentDirector?.signingRight !== nextDirector?.signingRight && isNotLastRow,
      dividerText: t(currentDirector.signingRight || ''),
    }),
  }

  const { isShowDivider, isBorderBottomHidden, dividerText } = dividerFactory[
    sortBy as keyof typeof dividerFactory
  ]()

  return (
    <>
      {isShowDivider && <TableDivider dividerText={dividerText} />}
      {React.cloneElement(children, { hideBorderBottom: isBorderBottomHidden })}
    </>
  )
}
